import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import clsx from "clsx";
import { saveVehicleChanges } from "../../../store/actions/custom";
import { setCurrentVehicleSelection } from "../../../store/actions/common";
import {
  setExtendableId,
  setExtendableType,
  setExtendableSaveDisabled
} from "../../../store/actions/edit";
import { remCalc, getHoursMins, timeToSeconds } from "../../../utils/helpers";
import { Box, makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import UnsavedChangesDialog from "../../dialogs/UnsavedChangesDialog";
import VehicleExpandablePanels from "./VehicleExpandablePanels";

const useStyles = makeStyles(theme => ({
  header: {
    borderBottom: theme.variables.border,
    minHeight: 82,
    boxSizing: "border-box"
  },
  title: {
    fontWeight: 500,
    fontSize: "1.25rem",
    lineHeight: "1.4375rem"
  },

  button: {
    padding: theme.spacing(1, 1.125),
    minWidth: remCalc(46)
  }
}));

function EditVehicle({
  setExtendableType,
  setExtendableId,
  vehicle,
  vehicleTypes,
  currentVehicleSelection,
  saveVehicleChanges,
  configuration,
  setCurrentVehicleSelection,
  saveDisabled,
  setExtendableSaveDisabled,
  expanded,
  setExpanded,
  editId,
  isViewMode,
  projectExpired,
  hasCompletedStops
}) {
  const quantity = 1;

  const classes = useStyles();

  const [changesDialog, setChangesDialog] = useState(false);

  const [errors, setErrors] = useState({
    name: "",
    startLocation: "",
    startTime: "",
    startBreak: "",
    breakDuration: ""
  });

  const [panelErrors, setPanelErrors] = useState({
    0: 0,
    1: 0,
    2: 0
  });

  const inputValuesRef = useRef();

  // Close edit panel
  const handleClose = () => {
    // setCurrentVehicleSelection([]);
    setExtendableType(null);
    setExtendableId(null);
  };

  const isNotAllowedToEdit =
    isViewMode || projectExpired || !!hasCompletedStops || vehicle?.locked;

  // Warn before closing
  const warnClose = saveDisabled ? handleClose : () => setChangesDialog(true);

  const handleSave = () => {
    const {
      startLocation,
      startTime,
      endTime,
      startBreak,
      endBreak,
      breakDuration
    } = inputValuesRef.current;

    const breakDifference =
      timeToSeconds(getHoursMins(endBreak)) -
      timeToSeconds(getHoursMins(startBreak));

    const errors = {
      name: "",
      capacity: "",
      startTime: "",
      startBreak: ""
    };

    const errorQueue = [];

    let summaryErrors = 0;
    let startEndErrors = 0;
    let lunchErrors = 0;

    if (
      !startLocation &&
      !(configuration.allow_reload && configuration.depot_address)
    ) {
      errors.startLocation = "Address is required";

      summaryErrors++;
      errorQueue.push(1);
    }

    if (endTime && getHoursMins(startTime) >= getHoursMins(endTime)) {
      errors.startTime = "Invalid time window";

      startEndErrors++;
      errorQueue.push(1);
    }

    if (startTime?.toString() === "Invalid Date") {
      errors.startTime = "Invalid time format";

      startEndErrors++;
      errorQueue.push(1);
    }

    if (endTime?.toString() === "Invalid Date") {
      errors.endTime = "Invalid time format";

      startEndErrors++;
      errorQueue.push(1);
    }

    if (
      (startBreak && !endBreak) ||
      (!startBreak && endBreak) ||
      (startBreak && endBreak && startBreak >= endBreak)
    ) {
      errors.startBreak = "Invalid time window";

      lunchErrors++;
      errorQueue.push(2);
    }

    if (startBreak && startTime > startBreak) {
      errors.startBreak = "Too soon";

      lunchErrors++;
      errorQueue.push(2);
    }

    let breakInvalid = false;

    if (startBreak?.toString() === "Invalid Date") {
      errors.startBreak = "Invalid time format";

      breakInvalid = true;

      lunchErrors++;
      errorQueue.push(2);
    }

    if (endBreak?.toString() === "Invalid Date") {
      errors.endBreak = "Invalid time format";

      breakInvalid = true;

      lunchErrors++;
      errorQueue.push(2);
    }

    if (endBreak && endTime && endTime < endBreak) {
      errors.endBreak = "Too late";

      lunchErrors++;
      errorQueue.push(2);
    }

    if (breakDuration && breakDuration < 1) {
      errors.breakDuration = "Must be a positive number";

      lunchErrors++;
      errorQueue.push(2);
    }

    if (startBreak && endBreak && !breakDuration) {
      errors.breakDuration = "Break duration is required";

      lunchErrors++;
      errorQueue.push(2);
    }

    if (
      !breakInvalid &&
      startBreak &&
      endBreak &&
      breakDuration &&
      breakDifference < Number(breakDuration) * 60
    ) {
      errors.breakDuration =
        "Break duration must be shorter than break interval";

      lunchErrors++;
      errorQueue.push(2);
    }

    const errorsStr = Object.keys(errors).reduce(
      (sum, el) => sum + errors[el],
      ""
    );

    if (!errorsStr) {
      saveVehicleChanges(inputValuesRef.current);
    } else {
      setExpanded(errorQueue);
    }
    setErrors(errors);
    setPanelErrors({ 0: summaryErrors, 1: startEndErrors, 2: lunchErrors });
  };

  // useEffect(() => {
  //   return () => {
  //     setCurrentVehicleSelection([]);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // Clear panel errors if expanded
  useEffect(() => {
    if (expanded !== null) setPanelErrors({ ...panelErrors, [expanded]: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      height="100%"
      id="project_drawer_edit_vehicle_dialog"
    >
      <Box
        className={classes.header}
        display="flex"
        alignItems="start"
        justifyContent="space-between"
        px={3}
        py={1.5}
      >
        <Box alignSelf="center">
          <Typography className={classes.title} variant="h5">
            {currentVehicleSelection.length > 0 && editId === "multiple"
              ? `Enable ${currentVehicleSelection.length || "1"} ${
                  currentVehicleSelection.length > 1 ? "vehicles" : "vehicle"
                }`
              : vehicle
              ? vehicle?.type_id?.name
              : "Enable a vehicle"}
          </Typography>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        flex="1 1 100%"
        overflow="hidden"
      >
        <VehicleExpandablePanels
          inputValuesRef={inputValuesRef}
          expanded={expanded}
          setExpanded={setExpanded}
          errors={errors}
          setErrors={setErrors}
          panelErrors={panelErrors}
          setPanelErrors={setPanelErrors}
          quantity={quantity}
          vehicle={vehicle}
          configuration={configuration}
          setSaveDisabled={setExtendableSaveDisabled}
          vehicleTypes={vehicleTypes}
          currentVehicleSelection={currentVehicleSelection}
          isNotAllowedToEdit={isNotAllowedToEdit}
        />

        {/* Cancel / Save */}
        <Box display="flex" justifyContent="flex-end" py={1.5} px={3} mt="auto">
          <Button
            className={classes.button}
            color="primary"
            onClick={warnClose}
            data-cy="cancelVehilceUpdate-project"
          >
            Cancel
          </Button>

          <Button
            className={clsx(classes.button, "vehicle-enable-button")}
            color="primary"
            onClick={handleSave}
            disabled={saveDisabled}
            data-cy="saveVehicleUpdate-project"
          >
            {(currentVehicleSelection.length > 0 && editId === "multiple") ||
            !vehicle
              ? "Enable"
              : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Unsaved changes dialog */}
      <UnsavedChangesDialog
        open={changesDialog}
        handleClose={() => setChangesDialog(false)}
        action={handleClose}
      />
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    vehicles: state.project.vehicles,
    vehicleTypes: state.project.vehicleTypes,
    extendableId: state.extendable.id,
    selectedVehicles: state.project.selectedVehicles,
    vehicle: state.project.vehicles.find(el => {
      return el.vehicle_id === state.extendable.id;
    }),
    configuration: state.project.vrp.configuration,
    saveDisabled: state.extendable.saveDisabled,
    currentVehicleSelection: state.common.currentVehicleSelection,
    isBreak: state.extendable.props.isBreak,
    editId: state.extendable.id,
    isViewMode:
      (state.project.published_once || state.project.published) &&
      state.project.status !== "optimized",
    projectExpired: ["expired", "finished"].includes(
      state.project.status.toLowerCase()
    ),
    hasCompletedStops: state.project.solution?.routes?.find(
      route =>
        route.vehicle_id === state.extendable.id &&
        route.stops.some(stop => stop.status === "finished")
    )
  };
}

export default connect(mapStateToProps, {
  setExtendableType,
  setExtendableId,
  saveVehicleChanges,
  setExtendableSaveDisabled,
  setCurrentVehicleSelection
})(EditVehicle);
